import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Business } from '../types';
import { fetchAllXMRBusinesses, getStoredBusinesses } from '../services/overpassApi';
import { fetchXMRStats } from '../services/cryptoApi';

export type SortOption = 'name' | 'category' | 'country' | 'rating' | 'reviews' | 'distance';
export type SortDirection = 'asc' | 'desc';

// Debounce utility function
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => { setDebouncedValue(value); }, delay);
    return () => { clearTimeout(handler); };
  }, [value, delay]);
  return debouncedValue;
};

export const useXMRBusinesses = (
  initialData: Business[] | null,
  viewport: { latitude: number; longitude: number; zoom: number },
  globalSearch: boolean = false,
  sortBy: SortOption = 'name',
  sortDirection: SortDirection = 'asc'
) => {
  const [allBusinesses, setAllBusinesses] = useState<Business[]>(initialData || []);
  const [loading, setLoading] = useState(!initialData); // Don't show loading if data is already provided
  const [error, setError] = useState<string | null>(null);
  const [xmrPrice, setXMRPrice] = useState<number | null>(null);

  const businessFetchController = useRef<AbortController | null>(null);
  const priceFetchController = useRef<AbortController | null>(null);

  const debouncedSortBy = useDebounce(sortBy, 300);
  const debouncedSortDirection = useDebounce(sortDirection, 300);

  // Function to fetch all businesses from the API
  const fetchAllBusinessData = useCallback(async (abortSignal: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const fetchedBusinesses = await fetchAllXMRBusinesses();
      const userBusinesses = getStoredBusinesses();
      if (!abortSignal.aborted) {
        setAllBusinesses([...fetchedBusinesses, ...userBusinesses]);
      }
    } catch (err: any) {
      if (!abortSignal.aborted) {
        setError(err instanceof Error ? err.message : 'Failed to load businesses');
        console.error("Error fetching business data:", err);
      }
    } finally {
      if (!abortSignal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  // Fetch data only if it's not passed in as a prop
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      setAllBusinesses(initialData);
      setLoading(false);
      return;
    }
    
    businessFetchController.current = new AbortController();
    const signal = businessFetchController.current.signal;

    fetchAllBusinessData(signal);

    return () => {
      businessFetchController.current?.abort();
    };
  }, [initialData, fetchAllBusinessData]);


  // Effect for XMR price fetching
  useEffect(() => {
    const fetchXMRPriceData = async (abortSignal: AbortSignal) => {
      try {
        const stats = await fetchXMRStats();
        if (!abortSignal.aborted) { setXMRPrice(stats.price.current_price); }
      } catch (err: any) {
        if (!abortSignal.aborted) { console.warn('Failed to load XMR price:', err); }
      }
    };
    
    priceFetchController.current = new AbortController();
    const signal = priceFetchController.current.signal;
    
    fetchXMRPriceData(signal);
    const priceInterval = setInterval(() => fetchXMRPriceData(signal), 5 * 60 * 1000);
    
    return () => {
      clearInterval(priceInterval);
      priceFetchController.current?.abort();
    };
  }, []);

  // Memoized filtering and sorting of businesses based on viewport and sort options
  const visibleBusinesses = useMemo(() => {
    let businessesToDisplay = allBusinesses;

    if (!globalSearch) {
      const zoomFactor = Math.max(0.01, 0.5 / Math.pow(2, viewport.zoom - 10));
      const south = viewport.latitude - zoomFactor;
      const north = viewport.latitude + zoomFactor;
      const west = viewport.longitude - zoomFactor;
      const east = viewport.longitude + zoomFactor;
      
      businessesToDisplay = allBusinesses.filter(b => 
        b.coordinates.lat >= south &&
        b.coordinates.lat <= north &&
        b.coordinates.lng >= west &&
        b.coordinates.lng <= east
      );
    }
    
    return sortBusinesses(businessesToDisplay, debouncedSortBy, debouncedSortDirection, viewport);
  }, [allBusinesses, globalSearch, viewport, debouncedSortBy, debouncedSortDirection]);

  // Manual refresh function
  const refreshBusinesses = useCallback(() => {
      if (businessFetchController.current) {
          businessFetchController.current.abort();
      }
      const newController = new AbortController();
      businessFetchController.current = newController;
      fetchAllBusinessData(newController.signal);
  }, [fetchAllBusinessData]);


  return { businesses: visibleBusinesses, loading, error, refreshBusinesses, xmrPrice };
};

// --- Helper Functions ---

const sortBusinesses = (
  businesses: Business[],
  sortBy: SortOption,
  direction: SortDirection,
  viewport: { latitude: number; longitude: number; zoom: number }
): Business[] => {
  // Prevent crash if businesses is not an array
  if (!Array.isArray(businesses)) {
    return [];
  }
  const sorted = [...businesses].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name': comparison = a.name.localeCompare(b.name); break;
      case 'category': comparison = a.category.localeCompare(b.category) || a.name.localeCompare(b.name); break;
      case 'country': comparison = extractCountry(a.address || '').localeCompare(extractCountry(b.address || '')) || a.name.localeCompare(b.name); break;
      case 'rating': comparison = (b.rating || 0) - (a.rating || 0) || (b.reviews || 0) - (a.reviews || 0); break;
      case 'reviews': comparison = (b.reviews || 0) - (a.reviews || 0) || (b.rating || 0) - (a.rating || 0); break;
      case 'distance':
        const distA = calculateDistance(viewport.latitude, viewport.longitude, a.coordinates.lat, a.coordinates.lng);
        const distB = calculateDistance(viewport.latitude, viewport.longitude, b.coordinates.lat, b.coordinates.lng);
        comparison = distA - distB;
        break;
      default: comparison = a.name.localeCompare(b.name);
    }
    return direction === 'desc' ? -comparison : comparison;
  });
  return sorted;
};

const extractCountry = (address: string): string => {
  if (!address) return '';
  const parts = address.split(',').map(part => part.trim());
  return parts[parts.length - 1] || '';
};

const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371; // km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};
